import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  GiftOutlined,
  HomeOutlined,
  RobotOutlined,
  StarOutlined,
  CameraOutlined,
  TeamOutlined,
  TrophyOutlined,
  TruckOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Col, Grid, Layout, Row, Typography } from 'antd';
import { logEvent } from 'firebase/analytics';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import appStoreBadge from '../../assets/images/Download_images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.jpg';
import googlePlayBadge from '../../assets/images/Download_images/GetItOnGooglePlay_Badge_Web_color_English.png';
import { analytics } from '../../firebase';

const { Title, Paragraph, Text } = Typography;
const { Content } = Layout;

const BRAND = {
  red: '#e83446',
  dark: '#222831',
  gradient: 'linear-gradient(135deg, #ff6f73 0%, #ff4757 100%)',
  gradientDark: 'linear-gradient(135deg, #222831 0%, #1a202c 100%)',
  redShadow: '0 8px 28px rgba(232,52,70,0.4)',
};

const Overview: React.FC = () => {
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (analytics) {
        logEvent(analytics as any, 'page_view', {
          page_title: 'Overview',
          page_location: window.location.href,
          page_path: window.location.pathname || '/overview',
          app_platform: 'web',
        });
      }
    } catch (_) {}
  }, []);

  const testimonials = [
    { name: 'Sarah Chen', location: 'Ames, Iowa', text: "Moving to a new city was stressful until I found Throwly. I turned my old dorm furniture into points and got everything I needed for my new apartment—without spending a dime!", avatar: 'SC' },
    { name: 'Marcus Johnson', location: 'Des Moines, Iowa', text: 'The AI pricing is incredible. No more guessing what my stuff is worth. I traded a bike I never used for a coffee maker I actually needed.', avatar: 'MJ' },
    { name: 'Lisa Rodriguez', location: 'Cedar Rapids, Iowa', text: "Finally, a marketplace that rewards giving! I love how my points can cover delivery too. It's like having a personal assistant for my stuff.", avatar: 'LR' },
    { name: 'David Kim', location: 'Iowa City, Iowa', text: "The community aspect is amazing. I've met so many neighbors through trading, and we're all helping reduce waste together.", avatar: 'DK' },
  ];

  const coreFeatures = [
    { icon: <RobotOutlined />, title: 'AI Pricing', description: 'Post an item and get an instant, fair point value' },
    { icon: <DollarOutlined />, title: 'Earn & Trade Points', description: 'Buyer debited, seller credited → no scammers, haggling, or lowballing' },
    { icon: <ClockCircleOutlined />, title: 'Save Time', description: 'Only serious buyers (with enough points) can contact you' },
    { icon: <TrophyOutlined />, title: 'Stack Points', description: 'Redeem for more expensive items. The more you give, the more you get' },
    { icon: <TruckOutlined />, title: 'Smooth Checkout', description: 'Pay for delivery with points. Uber-like algorithm calculates cost' },
    { icon: <HomeOutlined />, title: 'Thrift Store Partnerships', description: 'Use your points at local partners, do trade-ins' },
    { icon: <StarOutlined />, title: 'Leaderboards & Rewards', description: 'Recognizing top givers in the community' },
    { icon: <UserOutlined />, title: 'Request Items', description: 'Neighbors are incentivized to give' },
    { icon: <GiftOutlined />, title: 'Get Tips', description: 'Your good deeds can be seen and appreciated (like Venmo)' },
  ];

  const problemPoints = [
    "Endless 'Is this available?' messages that go nowhere",
    "Ghosting, no-shows, and wasted time",
    "Scammers and unsafe meetups",
    "Overpriced thrift stores taking advantage",
    "Items ending up in landfills instead of new homes",
  ];

  const solutionSteps = [
    { step: '1', title: 'List & Price', description: 'Snap a photo, AI suggests fair points instantly', icon: <RobotOutlined /> },
    { step: '2', title: 'Match & Connect', description: 'Only serious buyers with enough points can contact you', icon: <UserOutlined /> },
    { step: '3', title: 'Trade & Deliver', description: 'Points transfer instantly, delivery handled automatically', icon: <TruckOutlined /> },
  ];

  const floatingCards = [
    { icon: <RobotOutlined />, title: 'AI Pricing', sub: 'Fair values instantly' },
    { icon: <DollarOutlined />, title: 'Zero Fees', sub: 'Keep 100% value' },
    { icon: <TruckOutlined />, title: 'Uber Delivery', sub: 'Skip meetups' },
    { icon: <TeamOutlined />, title: 'Verified Community', sub: 'Safe & trusted' },
  ];

  const moreThanItems = [
    { icon: '🎁', title: 'Turn donations into real rewards', description: 'Transform your giving into points you can actually use for things you need' },
    { icon: '📊', title: 'Track giving for tax deductions', description: 'Keep detailed records of your charitable contributions for tax season' },
    { icon: '⚡', title: 'Remove marketplace friction', description: 'No more haggling, ghosting, or awkward meetups—just smooth exchanges' },
    { icon: '🤝', title: 'Build meaningful community', description: 'Connect through stories, local threads, and shared experiences' },
    { icon: '🗺️', title: 'Interactive community map', description: 'Find gigs, events, lost & found items, and curbside treasures nearby' },
    { icon: '🌱', title: 'Share, reuse, reduce waste', description: 'Join the movement toward sustainable living and circular economy' },
  ];

  const pad = screens.md ? '72px 60px' : '48px 28px';

  return (
    <Layout style={{ background: '#ffffff', minHeight: '100vh' }}>
      <style>{`
        .ov-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .ov-card:hover { transform: translateY(-6px); box-shadow: 0 20px 56px rgba(0,0,0,0.13) !important; }
        .ov-glass { transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease; }
        .ov-glass:hover { transform: translateY(-8px); background: rgba(255,255,255,0.18) !important; box-shadow: 0 24px 60px rgba(0,0,0,0.28) !important; }
        .ov-badge { transition: transform 0.2s ease; cursor: pointer; }
        .ov-badge:hover { transform: scale(1.05); }
      `}</style>

      {/* ── HERO ── */}
      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
          backgroundImage: `url('https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.38) 100%)' }} />
        <Content style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: screens.md ? '0 48px' : '0 24px', textAlign: 'center' }}>
            <Title
              level={1}
              style={{
                color: '#fff',
                marginBottom: 24,
                lineHeight: 1.1,
                fontSize: screens.md ? '72px' : '44px',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
              }}
            >
              Free. Sustainable. Rewarding.
            </Title>
            <Paragraph
              style={{
                fontSize: screens.md ? '22px' : '17px',
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 720,
                margin: '0 auto 48px',
                lineHeight: 1.55,
                textShadow: '0 2px 10px rgba(0,0,0,0.25)',
              }}
            >
              Turn your unused items into points. Trade with neighbors. Build community.{' '}
              <strong>No haggling. No scams. No waste.</strong>
            </Paragraph>

            {/* CTA Buttons */}
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 52 }}>
              <Button
                type="primary"
                size="large"
                icon={<CameraOutlined />}
                onClick={() => navigate('/ai-estimate')}
                style={{
                  background: BRAND.red,
                  border: 'none',
                  borderRadius: 14,
                  height: screens.md ? 60 : 48,
                  fontSize: screens.md ? 18 : 15,
                  fontWeight: 600,
                  padding: screens.md ? '0 32px' : '0 24px',
                  boxShadow: BRAND.redShadow,
                }}
              >
                Get a Price Estimate
              </Button>
              <Button
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={() => document.getElementById('solution')?.scrollIntoView({ behavior: 'smooth' })}
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  border: '2px solid rgba(255,255,255,0.4)',
                  color: '#fff',
                  borderRadius: 14,
                  height: screens.md ? 60 : 48,
                  fontSize: screens.md ? 18 : 15,
                  fontWeight: 600,
                  padding: screens.md ? '0 32px' : '0 24px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                Learn More
              </Button>
            </div>

            {/* Benefit cards — tilted absolute positioning on desktop, 2x2 grid on mobile */}
            <div style={{
              position: 'relative',
              width: '100%',
              height: screens.md ? '200px' : 'auto',
              marginBottom: screens.md ? '40px' : '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: screens.md ? '200px' : 'auto',
            }}>
              {screens.md ? (
                <>
                  <div style={{ position: 'absolute', top: '20px', left: '50px', background: 'rgba(255,255,255,0.95)', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '12px', minWidth: '180px', transform: 'rotate(-3deg)', zIndex: 2 }}>
                    <span style={{ color: BRAND.red, fontSize: 24 }}>{floatingCards[0].icon}</span>
                    <div><Text strong style={{ color: BRAND.dark, fontSize: 14, display: 'block' }}>{floatingCards[0].title}</Text><Text style={{ color: '#64748b', fontSize: 12 }}>{floatingCards[0].sub}</Text></div>
                  </div>
                  <div style={{ position: 'absolute', top: '10px', right: '50px', background: 'rgba(255,255,255,0.95)', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '12px', minWidth: '160px', transform: 'rotate(4deg)', zIndex: 2 }}>
                    <span style={{ color: BRAND.red, fontSize: 24 }}>{floatingCards[1].icon}</span>
                    <div><Text strong style={{ color: BRAND.dark, fontSize: 14, display: 'block' }}>{floatingCards[1].title}</Text><Text style={{ color: '#64748b', fontSize: 12 }}>{floatingCards[1].sub}</Text></div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '20px', left: '80px', background: 'rgba(255,255,255,0.95)', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '12px', minWidth: '170px', transform: 'rotate(-2deg)', zIndex: 2 }}>
                    <span style={{ color: BRAND.red, fontSize: 24 }}>{floatingCards[2].icon}</span>
                    <div><Text strong style={{ color: BRAND.dark, fontSize: 14, display: 'block' }}>{floatingCards[2].title}</Text><Text style={{ color: '#64748b', fontSize: 12 }}>{floatingCards[2].sub}</Text></div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '10px', right: '80px', background: 'rgba(255,255,255,0.95)', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '12px', minWidth: '180px', transform: 'rotate(3deg)', zIndex: 2 }}>
                    <span style={{ color: BRAND.red, fontSize: 24 }}>{floatingCards[3].icon}</span>
                    <div><Text strong style={{ color: BRAND.dark, fontSize: 14, display: 'block' }}>{floatingCards[3].title}</Text><Text style={{ color: '#64748b', fontSize: 12 }}>{floatingCards[3].sub}</Text></div>
                  </div>
                </>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', width: '100%', maxWidth: '320px', padding: '0 10px' }}>
                  {floatingCards.map((card, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.95)', padding: '12px 16px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <span style={{ color: BRAND.red, fontSize: 20, marginBottom: 8 }}>{card.icon}</span>
                      <Text strong style={{ color: BRAND.dark, fontSize: 12, marginBottom: 4, display: 'block' }}>{card.title}</Text>
                      <Text style={{ color: '#64748b', fontSize: 10 }}>{card.sub}</Text>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trust indicators */}
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: screens.md ? 20 : 12 }}>
              {['Verified Community', 'AI-Powered Pricing', 'Zero Fees'].map((label) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 999, padding: '10px 20px', backdropFilter: 'blur(8px)' }}>
                  <CheckCircleOutlined style={{ color: '#ff6f73', fontSize: 16 }} />
                  <Text style={{ color: '#fff', fontSize: screens.md ? 15 : 13, fontWeight: 600 }}>
                    {label}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </Content>
      </div>

      <Content style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>

        {/* ── PROBLEM ── */}
        <div id="problem" style={{ padding: '64px 0' }}>
          <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: 28, padding: pad, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 280, height: 280, background: `radial-gradient(circle, ${BRAND.red}10 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(40%, -40%)' }} />
            <Row gutter={[60, 48]} align="middle">
              <Col xs={24} lg={12}>
                <Title level={2} style={{ fontSize: screens.lg ? 48 : 32, fontWeight: 800, color: BRAND.dark, marginBottom: 20, lineHeight: 1.1 }}>
                  The Problem with<br /><span style={{ color: BRAND.red }}>Marketplaces Today</span>
                </Title>
                <Paragraph style={{ fontSize: 18, color: '#64748b', lineHeight: 1.65, marginBottom: 36 }}>
                  What used to be simple now feels like begging the algorithm. Endless messages, ghosting, overpriced thrift stores, and piles of stuff ending up in landfills.
                </Paragraph>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {problemPoints.map((point, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: 'rgba(255,255,255,0.88)', borderRadius: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(255,255,255,0.6)' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: BRAND.red, flexShrink: 0 }} />
                      <Text style={{ fontSize: 16, color: BRAND.dark, fontWeight: 500 }}>{point}</Text>
                    </div>
                  ))}
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                    alt="Frustrated person with phone"
                    style={{ width: '100%', maxWidth: 460, borderRadius: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.14)', transform: 'rotate(-2deg)' }}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── SOLUTION ── */}
        <div id="solution" style={{ paddingBottom: 64 }}>
          <div style={{ background: BRAND.gradientDark, borderRadius: 28, padding: pad, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: 360, height: 360, background: `radial-gradient(circle, ${BRAND.red}25 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(-50%, -50%)' }} />
            <Row gutter={[60, 48]} align="middle">
              <Col xs={24} lg={12}>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                    alt="Happy community trading"
                    style={{ width: '100%', maxWidth: 460, borderRadius: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', transform: 'rotate(2deg)' }}
                  />
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ paddingLeft: screens.lg ? 20 : 0 }}>
                  <Title level={2} style={{ fontSize: screens.lg ? 48 : 32, fontWeight: 800, color: '#fff', marginBottom: 20, lineHeight: 1.1 }}>
                    Throwly's<br /><span style={{ color: '#ff6f73' }}>Solution</span>
                  </Title>
                  <Paragraph style={{ fontSize: 18, color: '#e2e8f0', lineHeight: 1.65, marginBottom: 36 }}>
                    A point-based marketplace that makes giving rewarding, trading fair, and delivery effortless.
                  </Paragraph>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {solutionSteps.map((step, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 20, padding: '24px', background: 'rgba(255,255,255,0.08)', borderRadius: 18, border: '1px solid rgba(255,255,255,0.15)' }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: BRAND.red, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: BRAND.redShadow }}>
                          <Text style={{ color: '#fff', fontSize: 20, fontWeight: 800 }}>{step.step}</Text>
                        </div>
                        <div>
                          <Title level={4} style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{step.title}</Title>
                          <Paragraph style={{ fontSize: 15, color: '#e2e8f0', margin: 0, lineHeight: 1.65 }}>{step.description}</Paragraph>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── CORE FEATURES ── */}
        <div id="features" style={{ paddingBottom: 64 }}>
          <div style={{ background: '#fff', borderRadius: 28, padding: pad, boxShadow: '0 4px 32px rgba(0,0,0,0.07)', border: '1px solid #f0f4f8' }}>
            <Title level={2} style={{ fontSize: screens.md ? 44 : 30, fontWeight: 800, color: BRAND.dark, textAlign: 'center', marginBottom: 52 }}>
              What Makes Throwly Different
            </Title>
            <Row gutter={[60, 40]} align="middle">
              <Col xs={24} lg={12}>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=700&q=80"
                    alt="Community sharing and connection"
                    style={{ width: '100%', maxWidth: 420, height: 540, objectFit: 'cover', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.13)' }}
                  />
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {coreFeatures.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <span style={{ color: BRAND.red, fontSize: 20, lineHeight: 1.4, marginTop: 2, flexShrink: 0 }}>{f.icon}</span>
                      <Text style={{ fontSize: 15, color: BRAND.dark, lineHeight: 1.6 }}>
                        <strong>{f.title}</strong> — {f.description}
                      </Text>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── MISSION & VISION ── */}
        <div id="mission-vision" style={{ paddingBottom: 64 }}>
          <div style={{ background: BRAND.gradientDark, borderRadius: 28, padding: pad, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 400, height: 400, background: `radial-gradient(circle, ${BRAND.red}22 0%, transparent 70%)`, borderRadius: '50%', transform: 'translate(40%, -40%)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ textAlign: 'center', marginBottom: 56 }}>
                <Title level={1} style={{ color: '#fff', fontSize: screens.md ? 48 : 32, fontWeight: 800, marginBottom: 14, letterSpacing: '-0.02em' }}>
                  Our Mission & Vision
                </Title>
                <Paragraph style={{ color: 'rgba(255,255,255,0.78)', fontSize: 18, maxWidth: 600, margin: '0 auto', lineHeight: 1.65 }}>
                  Redefining the future of local commerce through sustainable, rewarding exchanges
                </Paragraph>
              </div>
              <Row gutter={[36, 36]}>
                <Col xs={24} lg={12}>
                  <div className="ov-glass" style={{ background: 'rgba(255,255,255,0.09)', backdropFilter: 'blur(20px)', borderRadius: 24, padding: screens.md ? '52px 44px' : '36px 28px', border: '1px solid rgba(255,255,255,0.18)', height: '100%' }}>
                    <div style={{ fontSize: 12, color: '#ff9fa2', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16 }}>Our Vision</div>
                    <Title level={2} style={{ color: '#fff', fontSize: 28, fontWeight: 700, marginBottom: 14, lineHeight: 1.2 }}>The Future of Local E-Commerce</Title>
                    <div style={{ fontSize: 20, color: '#ffb3b5', fontWeight: 600, marginBottom: 22, lineHeight: 1.3 }}>Free. Sustainable. Rewarding.</div>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, lineHeight: 1.8, margin: 0 }}>
                      We envision a world where every exchange builds community, where giving feels rewarding, and where sustainable commerce creates lasting value for everyone involved.
                    </Paragraph>
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div className="ov-glass" style={{ background: 'rgba(255,255,255,0.09)', backdropFilter: 'blur(20px)', borderRadius: 24, padding: screens.md ? '52px 44px' : '36px 28px', border: '1px solid rgba(255,255,255,0.18)', height: '100%' }}>
                    <div style={{ fontSize: 12, color: '#ff9fa2', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16 }}>Our Mission</div>
                    <Title level={2} style={{ color: '#fff', fontSize: 28, fontWeight: 700, marginBottom: 14, lineHeight: 1.2 }}>Transform Local Shopping</Title>
                    <div style={{ fontSize: 20, color: '#ffb3b5', fontWeight: 600, marginBottom: 22, lineHeight: 1.4 }}>Futuristic Facebook Marketplace</div>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, lineHeight: 1.8, margin: 0 }}>
                      We're revolutionizing local commerce by removing all friction from buying and selling. Through AI-powered pricing, point-based trading, and seamless delivery, we're creating the most rewarding marketplace experience ever built.
                    </Paragraph>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>

        {/* ── MORE THAN A MARKETPLACE ── */}
        <div id="more-than-marketplace" style={{ paddingBottom: 64 }}>
          <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)', borderRadius: 28, padding: pad, position: 'relative', overflow: 'hidden' }}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <Title level={1} style={{ color: BRAND.dark, fontSize: screens.md ? 44 : 30, fontWeight: 800, marginBottom: 14, letterSpacing: '-0.02em' }}>
                More Than Just a Marketplace
              </Title>
              <Paragraph style={{ color: '#64748b', fontSize: 18, maxWidth: 600, margin: '0 auto', lineHeight: 1.65 }}>
                We're building a community where giving creates lasting value and meaningful connections
              </Paragraph>
            </div>
            <Row gutter={[24, 24]}>
              {moreThanItems.map((item, i) => (
                <Col xs={24} md={12} lg={8} key={i}>
                  <div className="ov-card" style={{ background: 'rgba(255,255,255,0.92)', borderRadius: 22, padding: '36px 28px', border: '1px solid rgba(255,255,255,0.7)', boxShadow: '0 6px 24px rgba(0,0,0,0.07)', height: '100%', textAlign: 'center' }}>
                    <div style={{ fontSize: 44, marginBottom: 18 }}>{item.icon}</div>
                    <Title level={4} style={{ color: BRAND.dark, fontSize: 18, fontWeight: 700, marginBottom: 10, lineHeight: 1.3 }}>{item.title}</Title>
                    <Paragraph style={{ color: '#64748b', fontSize: 14, lineHeight: 1.65, margin: 0 }}>{item.description}</Paragraph>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* ── GETTING STARTED / IMPACT / PROMISE ── */}
        <div id="faq" style={{ paddingBottom: 64 }}>
          <div style={{ background: BRAND.gradientDark, borderRadius: 28, padding: pad, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.15)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ textAlign: 'center', marginBottom: 52 }}>
                <Title level={1} style={{ color: '#fff', fontSize: screens.md ? 48 : 32, fontWeight: 800, marginBottom: 14, letterSpacing: '-0.02em' }}>
                  Frequently Asked Questions
                </Title>
                <Paragraph style={{ color: 'rgba(255,255,255,0.78)', fontSize: 18, maxWidth: 600, margin: '0 auto', lineHeight: 1.65 }}>
                  Everything you need to know about Throwly
                </Paragraph>
              </div>

              <Row gutter={[28, 28]} style={{ marginBottom: 28 }}>
                <Col xs={24} lg={12}>
                  <div style={{ background: 'rgba(255,255,255,0.09)', backdropFilter: 'blur(20px)', borderRadius: 22, padding: screens.md ? '44px 36px' : '32px 24px', border: '1px solid rgba(255,255,255,0.16)', height: '100%' }}>
                    <Title level={3} style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>Getting Started</Title>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {[
                        { icon: '💰', text: '$50 worth of points at signup to help you get started fast' },
                        { icon: '💳', text: 'You can still sell for actual money if you prefer' },
                        { icon: '🔒', text: 'Security is enforced to avoid gaming the system' },
                      ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 18px', background: 'rgba(255,255,255,0.06)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}>
                          <div style={{ fontSize: 22, flexShrink: 0 }}>{item.icon}</div>
                          <Text style={{ color: 'rgba(255,255,255,0.88)', fontSize: 15, lineHeight: 1.5 }}>{item.text}</Text>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div style={{ background: 'rgba(255,255,255,0.09)', backdropFilter: 'blur(20px)', borderRadius: 22, padding: screens.md ? '44px 36px' : '32px 24px', border: '1px solid rgba(255,255,255,0.16)', height: '100%' }}>
                    <Title level={3} style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>Our Impact</Title>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {[
                        { icon: '🏪', text: 'Used by multiple consignment stores, over 100,000 users' },
                        { icon: '⭐', text: 'Partnered with major celebrities and environmental advocates' },
                        { icon: '🌍', text: 'Building a sustainable future through community giving' },
                      ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 18px', background: 'rgba(255,255,255,0.06)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}>
                          <div style={{ fontSize: 22, flexShrink: 0 }}>{item.icon}</div>
                          <Text style={{ color: 'rgba(255,255,255,0.88)', fontSize: 15, lineHeight: 1.5 }}>{item.text}</Text>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>

              <div style={{ background: 'rgba(255,255,255,0.09)', backdropFilter: 'blur(20px)', borderRadius: 22, padding: screens.md ? '44px 36px' : '32px 24px', border: '1px solid rgba(255,255,255,0.16)', textAlign: 'center' }}>
                <Title level={3} style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 16 }}>Our Promise</Title>
                <Paragraph style={{ color: 'rgba(255,255,255,0.84)', fontSize: 16, lineHeight: 1.85, margin: '0 auto', maxWidth: 760 }}>
                  Throwly isn't just about stuff—it's about empowering givers and making sure their good deeds are seen and rewarded. Unlike big donation chains (which take items for free, sell them high, and discard excess), Throwly ensures value flows back to the givers and the community.
                </Paragraph>
              </div>
            </div>
          </div>
        </div>

        {/* ── TESTIMONIALS ── */}
        <div id="testimonials" style={{ paddingBottom: 64 }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <Title level={2} style={{ fontSize: screens.md ? 48 : 32, fontWeight: 800, color: BRAND.dark, marginBottom: 14, lineHeight: 1.1 }}>
              Loved by Local<br /><span style={{ color: BRAND.red }}>Communities</span>
            </Title>
            <Paragraph style={{ fontSize: 18, color: '#64748b', lineHeight: 1.6, maxWidth: 520, margin: '0 auto' }}>
              Real stories from people building stronger communities through Throwly.
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {testimonials.map((t, i) => (
              <Col xs={24} md={12} key={i}>
                <div
                  className="ov-card"
                  style={{ background: '#fff', borderRadius: 22, padding: '36px', height: '100%', boxShadow: '0 6px 28px rgba(0,0,0,0.08)', border: '1px solid #f0f4f8', position: 'relative', overflow: 'hidden' }}
                >
                  <div style={{ position: 'absolute', top: -20, left: -20, width: 80, height: 80, background: `radial-gradient(circle, ${BRAND.red}12 0%, transparent 70%)`, borderRadius: '50%' }} />
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, position: 'relative', zIndex: 1 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: BRAND.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 6px 20px ${BRAND.red}30`, fontSize: 18, fontWeight: 700, color: '#fff' }}>
                      {t.avatar}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ position: 'relative', marginBottom: 18 }}>
                        <div style={{ position: 'absolute', top: -8, left: -14, fontSize: 44, color: BRAND.red, opacity: 0.15, fontFamily: 'serif', lineHeight: 1 }}>"</div>
                        <Text style={{ fontSize: 15, color: BRAND.dark, lineHeight: 1.7, fontStyle: 'italic', display: 'block' }}>{t.text}</Text>
                      </div>
                      <Text strong style={{ fontSize: 15, color: BRAND.dark }}>{t.name}</Text>
                      <br />
                      <Text style={{ fontSize: 13, color: '#64748b' }}>{t.location}</Text>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* ── CTA ── */}
        <div id="cta" style={{ paddingBottom: 64 }}>
          <div style={{ background: BRAND.gradientDark, borderRadius: 28, padding: screens.md ? '88px 60px' : '60px 28px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 30% 20%, ${BRAND.red}22 0%, transparent 50%), radial-gradient(circle at 70% 80%, ${BRAND.red}14 0%, transparent 50%)` }} />
            <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <Title level={2} style={{ fontSize: screens.md ? 56 : 34, fontWeight: 800, color: '#fff', marginBottom: 20, lineHeight: 1.1 }}>
                Ready to Transform<br /><span style={{ color: '#ff6f73' }}>Your Community?</span>
              </Title>
              <Paragraph style={{ fontSize: screens.md ? 20 : 16, color: '#e2e8f0', lineHeight: 1.65, maxWidth: 680, margin: '0 auto 52px' }}>
                Join thousands of neighbors who are already building a more sustainable, connected community through Throwly.
              </Paragraph>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 44 }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<CameraOutlined />}
                  onClick={() => navigate('/ai-estimate')}
                  style={{ background: BRAND.red, border: 'none', borderRadius: 18, height: screens.md ? 68 : 52, fontSize: screens.md ? 18 : 15, fontWeight: 700, padding: screens.md ? '0 40px' : '0 28px', boxShadow: BRAND.redShadow, letterSpacing: '0.3px' }}
                >
                  Get a Price Estimate
                </Button>
                <Button
                  size="large"
                  icon={<ArrowRightOutlined />}
                  onClick={() => {
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    const ua = navigator.userAgent.toLowerCase();
                    const url = isIOS || !ua.includes('android')
                      ? 'https://apps.apple.com/us/app/throwly/id1615326703'
                      : 'https://play.google.com/store/apps/details?id=com.Gutter';
                    window.open(url, '_blank');
                  }}
                  style={{ background: 'rgba(255,255,255,0.1)', border: `2px solid ${BRAND.red}`, color: BRAND.red, borderRadius: 18, height: screens.md ? 68 : 52, fontSize: screens.md ? 18 : 15, fontWeight: 700, padding: screens.md ? '0 40px' : '0 28px', backdropFilter: 'blur(20px)', letterSpacing: '0.3px' }}
                >
                  Download App
                </Button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                <img src={appStoreBadge} alt="Download on the App Store" className="ov-badge" style={{ height: screens.md ? 60 : 46, borderRadius: 10, objectFit: 'contain', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }} onClick={() => window.open('https://apps.apple.com/us/app/throwly/id1615326703', '_blank')} />
                <img src={googlePlayBadge} alt="Get it on Google Play" className="ov-badge" style={{ height: screens.md ? 60 : 46, borderRadius: 10, objectFit: 'contain', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }} onClick={() => window.open('https://play.google.com/store/apps/details?id=com.Gutter', '_blank')} />
              </div>
            </div>
          </div>
        </div>

      </Content>
    </Layout>
  );
};

export default Overview;
